/**
 * DoserneProjectJobs — scaffold job cards on a project's "Detaljer" tab.
 * Admin prepares jobs with the scaffold spec template + setup photos
 * (with an optional caption per photo) + status. A project can hold
 * several jobs. Labels follow the employee's chosen menu language (RO/NO/EN).
 */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, X, Pencil, Trash2, Camera, HardHat, Ruler, Layers,
  Anchor, Weight, ImageOff, Tag, ClipboardPaste, Copy, GripVertical,
} from "lucide-react";
import { useLang } from "./i18n";
import { useTranslated } from "./planTranslate";
import AdminDocsBox from "./AdminDocsBox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const computeArea = (length, height) => {
  const l = parseFloat(String(length ?? "").replace(",", ".").trim());
  const h = parseFloat(String(height ?? "").replace(",", ".").trim());
  if (!isNaN(l) && !isNaN(h) && l > 0 && h > 0) {
    return String(Math.round(l * h * 100) / 100);
  }
  return "";
};

const LABELS = {
  ro: {
    jobs: "Joburi", jobs_hint: "Joburi de schelă cu specificații și poze", add: "Adaugă job",
    empty: "Niciun job încă", empty_hint: "Apasă „Adaugă job” pentru a crea primul.",
    edit_title: "Editează job", new_title: "Job nou", title: "Titlu", title_ph: "Ex: Fațada nord",
    type: "Tip schelă", type_ph: "fațadă / turn / acoperiș", status: "Status",
    height: "Înălțime (m)", length: "Lungime (m)", width: "Lățime (m)", area: "Suprafață (m²)",
    wall: "Distanță de perete (m)", load: "Clasă încărcare", load_ph: "1–6",
    levels: "Nr. nivele", notes: "Note", save: "Salvează", saved: "Salvat", need_title: "Scrie un titlu",
    cant_save: "Nu s-a putut salva", cant_delete: "Nu s-a putut șterge", upload_failed: "Încărcarea a eșuat",
    del_q: "Ștergi acest job?", photos: "Poze", add_photo: "Adaugă", no_photos: "Nicio poză încă",
    caption_ph: "Comentariu…", not_started: "De început", in_progress: "În lucru", done: "Finalizat",
    sp_area: "Supraf.", sp_wall: "Perete", sp_class: "Clasă", sp_levels: "Nivele",
    area_auto: "Calculat: Lungime × Înălțime", custom: "Câmpuri personalizate", add_row: "Adaugă rând",
    field_label: "Etichetă", field_value: "Valoare", field_label_ph: "Ex: Tip ancoră", field_value_ph: "Ex: chimică",
    photos_now: "Poze (opțional)", paste_hint: "Trage poze aici, apasă «Adaugă» sau lipește cu Ctrl+V",
    dup: "Duplică", copy_suffix: "(copie)", dup_done: "Job duplicat",
  },
  no: {
    jobs: "Jobber", jobs_hint: "Stillasjobber med spesifikasjoner og bilder", add: "Legg til jobb",
    empty: "Ingen jobber ennå", empty_hint: "Trykk «Legg til jobb» for å opprette den første.",
    edit_title: "Rediger jobb", new_title: "Ny jobb", title: "Tittel", title_ph: "F.eks. Fasade nord",
    type: "Stillastype", type_ph: "fasade / tårn / tak", status: "Status",
    height: "Høyde (m)", length: "Lengde (m)", width: "Bredde (m)", area: "Areal (m²)",
    wall: "Veggavstand (m)", load: "Lastklasse", load_ph: "1–6",
    levels: "Antall nivåer", notes: "Notater", save: "Lagre", saved: "Lagret", need_title: "Skriv en tittel",
    cant_save: "Kunne ikke lagre", cant_delete: "Kunne ikke slette", upload_failed: "Opplasting mislyktes",
    del_q: "Slette denne jobben?", photos: "Bilder", add_photo: "Legg til", no_photos: "Ingen bilder ennå",
    caption_ph: "Kommentar…", not_started: "Ikke startet", in_progress: "Pågår", done: "Ferdig",
    sp_area: "Areal", sp_wall: "Vegg", sp_class: "Klasse", sp_levels: "Nivåer",
    area_auto: "Beregnet: Lengde × Høyde", custom: "Egendefinerte felt", add_row: "Legg til rad",
    field_label: "Etikett", field_value: "Verdi", field_label_ph: "F.eks. Forankringstype", field_value_ph: "F.eks. kjemisk",
    photos_now: "Bilder (valgfritt)", paste_hint: "Slipp bilder her, trykk «Legg til» eller lim inn med Ctrl+V",
    dup: "Dupliser", copy_suffix: "(kopi)", dup_done: "Jobb duplisert",
  },
  en: {
    jobs: "Jobs", jobs_hint: "Scaffold jobs with specs and photos", add: "Add job",
    empty: "No jobs yet", empty_hint: "Tap “Add job” to create the first.",
    edit_title: "Edit job", new_title: "New job", title: "Title", title_ph: "E.g. North facade",
    type: "Scaffold type", type_ph: "facade / tower / roof", status: "Status",
    height: "Height (m)", length: "Length (m)", width: "Width (m)", area: "Area (m²)",
    wall: "Wall distance (m)", load: "Load class", load_ph: "1–6",
    levels: "Levels", notes: "Notes", save: "Save", saved: "Saved", need_title: "Enter a title",
    cant_save: "Could not save", cant_delete: "Could not delete", upload_failed: "Upload failed",
    del_q: "Delete this job?", photos: "Photos", add_photo: "Add", no_photos: "No photos yet",
    caption_ph: "Comment…", not_started: "Not started", in_progress: "In progress", done: "Done",
    sp_area: "Area", sp_wall: "Wall", sp_class: "Class", sp_levels: "Levels",
    area_auto: "Computed: Length × Height", custom: "Custom fields", add_row: "Add row",
    field_label: "Label", field_value: "Value", field_label_ph: "E.g. Anchor type", field_value_ph: "E.g. chemical",
    photos_now: "Photos (optional)", paste_hint: "Drop photos here, tap “Add” or paste with Ctrl+V",
    dup: "Duplicate", copy_suffix: "(copy)", dup_done: "Job duplicated",
  },
};

const STATUS_CLS = {
  not_started: "bg-slate-100 text-slate-600 border-slate-200",
  in_progress: "bg-amber-100 text-amber-700 border-amber-300",
  done: "bg-emerald-100 text-emerald-700 border-emerald-300",
};

const EMPTY = {
  title: "", scaffold_type: "", height_m: "", length_m: "", width_m: "",
  area_m2: "", wall_distance_m: "", load_class: "", levels: "",
  notes: "", status: "not_started", custom_fields: [], admin_docs: [],
};

function Spec({ Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</span>
      <span className="text-sm text-slate-800 font-semibold">{value}</span>
    </div>
  );
}

function JobForm({ initial, T, onClose, onSave, authHeader, isAdmin }) {
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}), custom_fields: (initial?.custom_fields || []), admin_docs: (initial?.admin_docs || []) });
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState([]); // File[] to upload after save
  const fileRef = React.useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const area = computeArea(form.length_m, form.height_m);

  const addFiles = (files) => {
    const imgs = Array.from(files).filter((f) => f.type?.startsWith("image/"));
    if (imgs.length) setPending((p) => [...p, ...imgs]);
  };

  React.useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      const imgs = [];
      for (const it of items) {
        if (it.type?.startsWith("image/")) { const f = it.getAsFile(); if (f) imgs.push(f); }
      }
      if (imgs.length) { e.preventDefault(); addFiles(imgs); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  const setCustom = (i, key) => (e) => setForm((f) => {
    const cf = [...(f.custom_fields || [])];
    cf[i] = { ...cf[i], [key]: e.target.value };
    return { ...f, custom_fields: cf };
  });
  const addRow = () => setForm((f) => ({ ...f, custom_fields: [...(f.custom_fields || []), { label: "", value: "" }] }));
  const removeRow = (i) => setForm((f) => ({ ...f, custom_fields: (f.custom_fields || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.title.trim()) return toast.error(T.need_title);
    setBusy(true);
    const payload = {
      ...form,
      area_m2: area || form.area_m2,
      custom_fields: (form.custom_fields || []).filter((c) => (c.label || "").trim() || (c.value || "").trim()),
    };
    await onSave(payload, pending);
    setBusy(false);
  };

  const field = (k, label, ph) => (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{label}</label>
      <input value={form[k]} onChange={set(k)} placeholder={ph || ""}
             className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
             data-testid={`job-field-${k}`} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="job-form-dialog">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-200 flex items-center justify-between z-10">
          <h2 className="text-xl font-black text-slate-900">{initial ? T.edit_title : T.new_title}</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-4">
          {field("title", T.title, T.title_ph)}
          <div className="grid grid-cols-2 gap-3">
            {field("scaffold_type", T.type, T.type_ph)}
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{T.status}</label>
              <select value={form.status} onChange={set("status")} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="job-field-status">
                {["not_started", "in_progress", "done"].map((k) => <option key={k} value={k}>{T[k]}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {field("height_m", T.height)}
            {field("length_m", T.length)}
            {field("width_m", T.width)}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{T.area}</label>
              <input value={area ? `${area} m²` : ""} readOnly placeholder="—"
                     className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-700 font-bold" data-testid="job-field-area_m2" />
              <div className="text-[9px] text-slate-400 mt-0.5">{T.area_auto}</div>
            </div>
            {field("wall_distance_m", T.wall)}
            {field("load_class", T.load, T.load_ph)}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {field("levels", T.levels)}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black">{T.custom}</label>
              <button type="button" onClick={addRow} className="inline-flex items-center gap-1 text-xs font-black text-cyan-700 hover:text-cyan-900" data-testid="job-add-row-btn"><Plus className="w-3.5 h-3.5" /> {T.add_row}</button>
            </div>
            <div className="space-y-2">
              {(form.custom_fields || []).map((cf, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input value={cf.label || ""} onChange={setCustom(i, "label")} placeholder={T.field_label_ph}
                         className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid={`job-custom-label-${i}`} />
                  <input value={cf.value || ""} onChange={setCustom(i, "value")} placeholder={T.field_value_ph}
                         className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid={`job-custom-value-${i}`} />
                  <button type="button" onClick={() => removeRow(i)} className="w-9 h-9 rounded-lg border border-red-200 hover:bg-red-50 flex items-center justify-center text-red-600 flex-shrink-0" data-testid={`job-custom-remove-${i}`}><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black">{T.photos_now}</label>
              <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1 text-xs font-black text-cyan-700 hover:text-cyan-900" data-testid="job-form-add-photos"><Camera className="w-3.5 h-3.5" /> {T.add_photo}</button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="job-form-file-input" />
            <div className="flex items-center gap-1.5 text-[10px] text-slate-400 mb-2"><ClipboardPaste className="w-3.5 h-3.5" /> {T.paste_hint}</div>
            {pending.length ? (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {pending.map((f, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200">
                    <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setPending((p) => p.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/90 flex items-center justify-center" style={{ color: "#fff" }} data-testid={`job-pending-del-${i}`}><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{T.notes}</label>
            <textarea value={form.notes} onChange={set("notes")} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="job-field-notes" />
          </div>
          {isAdmin && (
            <AdminDocsBox
              docs={form.admin_docs || []}
              authHeader={authHeader}
              onChange={(newDocs) => setForm((f) => ({ ...f, admin_docs: newDocs }))}
              title="Tilbud & dokumenter (kun admin)"
            />
          )}
          <button onClick={save} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-sm font-black" style={{ color: "#fff" }} data-testid="job-save-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {T.save}
          </button>
        </div>
      </div>
    </div>
  );
}

function JobCard({ job, isAdmin, authHeader, T, onEdit, onDeleted, onChanged, onOpenPhoto, onDuplicate, index, onDragStart, onDragEnter, onDragEnd, dragging }) {
  const [busy, setBusy] = useState(false);
  const st = STATUS_CLS[job.status] || STATUS_CLS.not_started;
  const tTitle = useTranslated(job.title, authHeader);
  const tNotes = useTranslated(job.notes, authHeader);

  const del = async () => {
    if (!window.confirm(T.del_q)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/doserne/projects/${job.project_id}/jobs/${job.id}`, authHeader());
      onDeleted(job.id);
    } catch (e) { toast.error(e?.response?.data?.detail || T.cant_delete); }
    setBusy(false);
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await axios.post(`${API}/doserne/projects/${job.project_id}/jobs/${job.id}/photos`, fd, {
        ...authHeader(), headers: { ...authHeader().headers, "Content-Type": "multipart/form-data" },
      });
      onChanged(data);
    } catch (err) { toast.error(err?.response?.data?.detail || T.upload_failed); }
    setBusy(false);
    e.target.value = "";
  };

  const removePhoto = async (url) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/doserne/projects/${job.project_id}/jobs/${job.id}/photos/remove`, { url }, authHeader());
      onChanged(data);
    } catch { /* silent */ }
    setBusy(false);
  };

  const setCaption = async (url, caption) => {
    try {
      const { data } = await axios.post(`${API}/doserne/projects/${job.project_id}/jobs/${job.id}/photos/caption`, { url, caption }, authHeader());
      onChanged(data);
    } catch { /* silent */ }
  };

  return (
    <div
      className={`bg-white border rounded-2xl p-5 transition ${dragging ? "border-cyan-400 shadow-lg opacity-80" : "border-slate-200"}`}
      data-testid={`job-card-${job.id}`}
      draggable={isAdmin}
      onDragStart={() => onDragStart?.(index)}
      onDragEnter={() => onDragEnter?.(index)}
      onDragEnd={() => onDragEnd?.()}
      onDragOver={(e) => e.preventDefault()}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {isAdmin && <GripVertical className="w-4 h-4 text-slate-300 hover:text-slate-500 cursor-grab flex-shrink-0" data-testid={`job-drag-${job.id}`} />}
          <div className="w-9 h-9 rounded-xl bg-cyan-50 border border-cyan-200 flex items-center justify-center flex-shrink-0">
            <HardHat className="w-5 h-5 text-cyan-700" />
          </div>
          <div className="min-w-0">
            <div className="text-base font-black text-slate-900 truncate">{tTitle}</div>
            {job.scaffold_type && <div className="text-xs text-slate-500">{job.scaffold_type}</div>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className={`text-[9px] uppercase tracking-widest font-black rounded-full px-2 py-1 border ${st}`} data-testid={`job-status-${job.id}`}>{T[job.status]}</span>
          {isAdmin && (
            <>
              <button onClick={() => onEdit(job)} className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid={`job-edit-${job.id}`}><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={() => onDuplicate(job)} className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-100 flex items-center justify-center text-slate-600" title={T.dup} data-testid={`job-duplicate-${job.id}`}><Copy className="w-3.5 h-3.5" /></button>
              <button onClick={del} disabled={busy} className="w-8 h-8 rounded-lg border border-red-200 hover:bg-red-50 flex items-center justify-center text-red-600" data-testid={`job-delete-${job.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 py-3 border-y border-slate-100">
        <Spec Icon={Ruler} label="H" value={job.height_m && `${job.height_m} m`} />
        <Spec Icon={Ruler} label="L" value={job.length_m && `${job.length_m} m`} />
        <Spec Icon={Ruler} label="B" value={job.width_m && `${job.width_m} m`} />
        <Spec Icon={Layers} label={T.sp_area} value={job.area_m2 && `${job.area_m2} m²`} />
        <Spec Icon={Anchor} label={T.sp_wall} value={job.wall_distance_m && `${job.wall_distance_m} m`} />
        <Spec Icon={Weight} label={T.sp_class} value={job.load_class} />
        <Spec Icon={Layers} label={T.sp_levels} value={job.levels} />
        {(job.custom_fields || []).map((cf, i) => (cf.label || cf.value) ? (
          <Spec key={i} Icon={Tag} label={cf.label} value={cf.value} />
        ) : null)}
      </div>

      {job.notes && <div className="text-sm text-slate-600 whitespace-pre-wrap mt-3">{tNotes}</div>}
      {isAdmin && (job.admin_docs || []).length > 0 && (
        <div className="mt-3">
          <AdminDocsBox docs={job.admin_docs} authHeader={authHeader} readOnly title="Tilbud & dokumenter (kun admin)" />
        </div>
      )}

      <div className="mt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">{T.photos} {job.photos?.length ? `· ${job.photos.length}` : ""}</div>
          {isAdmin && (
            <button onClick={() => document.getElementById(`job-file-${job.id}`)?.click()} disabled={busy} className="inline-flex items-center gap-1 text-xs font-black text-cyan-700 hover:text-cyan-900" data-testid={`job-add-photos-${job.id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />} {T.add_photo}
            </button>
          )}
          <input id={`job-file-${job.id}`} type="file" accept="image/*" multiple className="hidden" onChange={uploadPhotos} data-testid={`job-file-input-${job.id}`} />
        </div>
        {job.photos?.length ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {job.photos.map((ph, i) => (
              <div key={ph.url} className="group">
                <div className="relative aspect-square rounded-lg overflow-hidden border border-slate-200">
                  <button type="button" onClick={() => onOpenPhoto(ph.url, ph.caption)} className="w-full h-full block" data-testid={`job-photo-open-${job.id}-${i}`}><img src={ph.url} alt={ph.caption || ""} className="w-full h-full object-cover" loading="lazy" /></button>
                  {isAdmin && (
                    <button onClick={() => removePhoto(ph.url)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition" style={{ color: "#fff" }} data-testid={`job-photo-del-${job.id}-${i}`}><X className="w-3.5 h-3.5" /></button>
                  )}
                </div>
                {isAdmin ? (
                  <input defaultValue={ph.caption || ""} placeholder={T.caption_ph}
                         onBlur={(e) => e.target.value !== (ph.caption || "") && setCaption(ph.url, e.target.value)}
                         className="mt-1 w-full text-[11px] px-1.5 py-1 border border-slate-200 rounded focus:border-cyan-400 outline-none"
                         data-testid={`job-photo-caption-${job.id}-${i}`} />
                ) : ph.caption ? (
                  <div className="mt-1 text-[11px] text-slate-600 leading-snug">{ph.caption}</div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-slate-400 py-2"><ImageOff className="w-4 h-4" /> {T.no_photos}</div>
        )}
      </div>
    </div>
  );
}

export default function DoserneProjectJobs({ projectId, isAdmin, authHeader, onTotalArea }) {
  const { lang } = useLang();
  const T = LABELS[lang] || LABELS.no;
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/projects/${projectId}/jobs`, authHeader());
      setJobs(data.jobs || []);
    } catch { /* silent */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [projectId]);

  // Refresh when Jarvis (or another surface) adds a line to this project's overview.
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener("doserne:project-jobs-changed", onChanged);
    return () => window.removeEventListener("doserne:project-jobs-changed", onChanged);
  }, [projectId]); // eslint-disable-line

  useEffect(() => {
    if (!onTotalArea) return;
    const sum = jobs.reduce((acc, j) => {
      const n = parseFloat(String(j.area_m2 ?? "").replace(",", ".").trim());
      return acc + (isNaN(n) ? 0 : n);
    }, 0);
    onTotalArea(Math.round(sum * 100) / 100);
  }, [jobs]);

  const duplicate = async (job) => {
    const payload = {
      title: `${job.title} ${T.copy_suffix}`.trim(),
      scaffold_type: job.scaffold_type || "", height_m: job.height_m || "", length_m: job.length_m || "",
      width_m: job.width_m || "", area_m2: job.area_m2 || "", wall_distance_m: job.wall_distance_m || "",
      load_class: job.load_class || "", levels: job.levels || "", notes: job.notes || "",
      status: "not_started", custom_fields: job.custom_fields || [],
    };
    try {
      const { data } = await axios.post(`${API}/doserne/projects/${projectId}/jobs`, payload, authHeader());
      setJobs((prev) => [...prev, data]);
      toast.success(T.dup_done);
    } catch (e) { toast.error(e?.response?.data?.detail || T.cant_save); }
  };

  // Drag-and-drop reordering (admin)
  const jobsRef = useRef(jobs);
  useEffect(() => { jobsRef.current = jobs; }, [jobs]);
  const dragIdx = useRef(null);
  const [dragging, setDragging] = useState(null);

  const onDragStart = (i) => { dragIdx.current = i; setDragging(i); };
  const onDragEnter = (i) => {
    const from = dragIdx.current;
    if (from === null || from === i) return;
    setJobs((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
    dragIdx.current = i;
    setDragging(i);
  };
  const onDragEnd = async () => {
    dragIdx.current = null;
    setDragging(null);
    try {
      await axios.post(`${API}/doserne/projects/${projectId}/jobs/reorder`, { job_ids: jobsRef.current.map((j) => j.id) }, authHeader());
    } catch { /* silent */ }
  };

  const save = async (form, pending = []) => {
    try {
      let job;
      if (editing) {
        const { data } = await axios.put(`${API}/doserne/projects/${projectId}/jobs/${editing.id}`, form, authHeader());
        job = data;
      } else {
        const { data } = await axios.post(`${API}/doserne/projects/${projectId}/jobs`, form, authHeader());
        job = data;
      }
      if (pending && pending.length) {
        const fd = new FormData();
        pending.forEach((f) => fd.append("files", f));
        const { data } = await axios.post(`${API}/doserne/projects/${projectId}/jobs/${job.id}/photos`, fd, {
          ...authHeader(), headers: { ...authHeader().headers, "Content-Type": "multipart/form-data" },
        });
        job = data;
      }
      setJobs((prev) => (prev.some((j) => j.id === job.id) ? prev.map((j) => (j.id === job.id ? job : j)) : [...prev, job]));
      setFormOpen(false); setEditing(null);
      toast.success(T.saved);
    } catch (e) { toast.error(e?.response?.data?.detail || T.cant_save); }
  };

  return (
    <div className="space-y-4" data-testid="project-jobs-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-lg font-black text-slate-900">{T.jobs}</div>
          <div className="text-xs text-slate-500">{T.jobs_hint}</div>
        </div>
        {isAdmin && (
          <button onClick={() => { setEditing(null); setFormOpen(true); }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-xs font-black uppercase tracking-widest" style={{ color: "#fff" }} data-testid="job-add-btn">
            <Plus className="w-3.5 h-3.5" /> {T.add}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : jobs.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center">
          <HardHat className="w-9 h-9 text-slate-300 mx-auto mb-2" />
          <div className="text-sm font-black text-slate-700">{T.empty}</div>
          {isAdmin && <div className="text-xs text-slate-500 mt-1">{T.empty_hint}</div>}
        </div>
      ) : (
        jobs.map((job, i) => (
          <JobCard key={job.id} job={job} isAdmin={isAdmin} authHeader={authHeader} T={T}
                   index={i} dragging={dragging === i}
                   onDragStart={onDragStart} onDragEnter={onDragEnter} onDragEnd={onDragEnd}
                   onEdit={(j) => { setEditing(j); setFormOpen(true); }}
                   onDeleted={(jid) => setJobs((prev) => prev.filter((j) => j.id !== jid))}
                   onDuplicate={duplicate}
                   onOpenPhoto={(url, caption) => setLightbox({ url, caption })}
                   onChanged={(updated) => setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)))} />
        ))
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex flex-col items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="job-lightbox">
          <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center" style={{ color: "#fff" }} onClick={() => setLightbox(null)} data-testid="job-lightbox-close"><X className="w-5 h-5" /></button>
          <img src={lightbox.url} alt={lightbox.caption || ""} className="max-w-full max-h-[82vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          {lightbox.caption ? (
            <div className="mt-3 max-w-2xl text-center text-sm text-white/90 bg-black/40 rounded-lg px-4 py-2" onClick={(e) => e.stopPropagation()} data-testid="job-lightbox-caption">{lightbox.caption}</div>
          ) : null}
        </div>
      )}

      {formOpen && (
        <JobForm initial={editing} T={T} onClose={() => { setFormOpen(false); setEditing(null); }} onSave={save} authHeader={authHeader} isAdmin={isAdmin} />
      )}
    </div>
  );
}
